/**
 * `ChartFrame` — the wrapper every chart on the platform sits in (launch round, 2026-10-07:
 * "for all charts (graphs and pie charts etc) in the system, we need to make them
 * expandable. It is very important.").
 *
 * Inline, it draws its chart exactly as the page always did and adds one thing: a small
 * expand button in the top-right corner. The chart is a **render-prop**, `children(size)`,
 * so the lightbox can draw the same chart again at its own size — the same component and
 * the same data, laid out for 1,400 px rather than 290. Inline the size is `{w: 0, h: 0,
 * expanded: false}` and the chart keeps the width its call site always gave it; callers use
 * `chartW(size, inlineW)` / `chartH(size, inlineH)` to pick between the two.
 *
 * The frame also carries the chart's axis titles (`xLabel`, `yLabel`) down to the
 * primitive through `ChartAxisContext`, so a call site states them once.
 *
 * `title`, `subtitle` and `legend` are the lightbox's: the page already heads each chart
 * with its card, so inline nothing of them is drawn.
 */
import { useEffect, useId, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { ChartAxisContext } from './axisContext';
import {
  closeChartLightbox,
  openChartLightbox,
  readChartLightbox,
  subscribeChartLightbox,
  updateChartLightbox,
  type ChartSize,
} from './chartLightboxStore';
import { useChartWidth } from './useChartWidth';

export interface ChartFrameProps {
  /** The chart's name, the lightbox heading. */
  title: string;
  subtitle?: string;
  /** Shown beside the chart in the lightbox (the page draws its own legend inline). */
  legend?: ReactNode;
  /** "quantity (unit)" under the x axis, e.g. "Year" or "Share of households (%)". */
  xLabel?: string;
  /** "quantity (unit)" beside the y axis, e.g. "Producers" or "Country". */
  yLabel?: string;
  children: (size: ChartSize) => ReactNode;
  /** The expand button carries `data-testid="{testId}-expand"`. */
  testId?: string;
  /** The expand button's accessible name; `charts.expand` by default. */
  expandLabel?: string;
  className?: string;
}

export function ChartFrame({
  title,
  subtitle,
  legend,
  xLabel,
  yLabel,
  children,
  testId,
  expandLabel,
  className,
}: ChartFrameProps) {
  const { t } = useTranslation();
  const key = useId();
  const button = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  // The frame's own content width, handed to the chart inline (#749). 0 until measured.
  const width = useChartWidth(box, 0);
  const inline = useMemo<ChartSize>(() => ({ w: width, h: 0, expanded: false }), [width]);
  const isOpen = useSyncExternalStore(
    subscribeChartLightbox,
    () => readChartLightbox()?.key === key,
    () => false,
  );

  // While this frame owns the dialog, keep its copy of the chart current: the page may
  // re-render under it (a filter changed, a query landed) and the lightbox must follow.
  useEffect(() => {
    if (isOpen) updateChartLightbox(key, { title, subtitle, legend, xLabel, yLabel, render: children });
  });

  // A frame that leaves the page takes its dialog with it.
  useEffect(
    () => () => {
      if (readChartLightbox()?.key === key) closeChartLightbox(false);
    },
    [key],
  );

  const label = expandLabel ?? t('charts.expand');

  return (
    <div ref={box} className={`chart-frame${className ? ` ${className}` : ''}`} data-chart-frame={testId ?? ''}>
      <ChartAxisContext.Provider value={{ xLabel, yLabel }}>{children(inline)}</ChartAxisContext.Provider>
      {/* After the chart in the DOM, so a reader's (and a test's) first `svg` in the frame is
          the chart's own. The CSS reserves a row of its own above the chart and puts the
          button at the LEFT of it (client, 7 Oct 2026: wherever a corner overlay was "not
          ideal", move it to the left and make it visible), so it never sits on a label, a
          tick or the plot, on any chart kind. It is a visible labelled button, not a glyph. */}
      <button
        ref={button}
        type="button"
        className="chart-frame-expand"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid={testId ? `${testId}-expand` : 'chart-expand'}
        onClick={() =>
          openChartLightbox({
            key,
            title,
            subtitle,
            legend,
            xLabel,
            yLabel,
            render: children,
            opener: button.current,
          })
        }
      >
        <Icon name="expand" size={12} />
        <span className="chart-frame-expand-text">{label}</span>
      </button>
    </div>
  );
}

export default ChartFrame;
